import { Lead, GuideSection, P, UL, LI, Callout, GuideLink, Term } from './guide-ui';

/**
 * Nederlandstalige gids (locale: 'nl', logpulse.nl): datalekken die met een
 * inlog begonnen en de inlogsignalen die je kunt bewaken. Hoort bij de
 * dreigingspagina en de ChatGPT-campagne, groep B. Elke genoemde detectie is
 * een ingebouwde detectie; de beschrijving volgt wat de query echt telt.
 */
export function InlogLekkenGuideBody() {
  return (
    <>
      <Lead>
        Een oud account met wachtwoord 123456, een gestolen inlog, password
        spraying bij honderden organisaties: een aantal van de grootste
        incidenten van oktober 2026 had geen zero-day nodig. Alleen een geldige
        inlog. Deze gids laat zien welke inlogsignalen het bewaken waard zijn en
        uit welke logs ze komen.
      </Lead>

      <GuideSection id="wat-er-gebeurde" title="Wat er gebeurde">
        <P>
          In Denemarken kwamen aanvallers bij het nationale CPR-register via een
          oud account met het wachtwoord 123456. Bij ASOS ging het mis met een
          gestolen inlog. De ransomwaregroep SafePay trof meer dan 600
          organisaties, grotendeels via gestolen wachtwoorden en brute force (
          <GuideLink href="https://www.cm-alliance.com/cybersecurity-blog/biggest-cyber-attacks-data-breaches-ransomware-attacks-october-2026">
            overzicht van CM-Alliance
          </GuideLink>
          ).
        </P>
        <P>
          Geen van deze aanvallen had een onbekend lek nodig. Ze hadden een
          geldige inlog nodig en elke inlog laat een spoor achter bij je
          identity provider.
        </P>
      </GuideSection>

      <GuideSection id="signalen" title="Vijf inlogsignalen die een melding waard zijn">
        <UL>
          <LI>
            <Term>Password spraying.</Term> Eén bron probeert een paar veelgebruikte
            wachtwoorden op veel accounts, net onder de lockout-grens. De
            ingebouwde detectie telt op hoeveel verschillende accounts één IP
            faalt.
          </LI>
          <LI>
            <Term>Brute force op één account.</Term> Veel mislukte pogingen op
            hetzelfde account. Kijk bij een melding of er daarna een geslaagde
            inlog volgde, want daar gaat het om.
          </LI>
          <LI>
            <Term>MFA-moeheid.</Term> Een reeks pushmeldingen naar één gebruiker,
            tot die er eentje goedkeurt. LogPulse meldt herhaald mislukte
            MFA-pogingen op één account.
          </LI>
          <LI>
            <Term>MFA uitgezet of een beheerdersrol toegekend.</Term> Eenmaal
            binnen verzwakken aanvallers vaak het account of geven ze het
            beheerrechten. Beide zijn ingebouwde detecties op de auditlogs.
          </LI>
          <LI>
            <Term>Legacy authenticatie.</Term> Oude protocollen zoals IMAP, POP3
            of SMTP met basic auth slaan MFA helemaal over. LogPulse meldt zulke
            inlogs in Entra ID; ze zijn het weten waard, ook als ze onschuldig
            zijn.
          </LI>
        </UL>
      </GuideSection>

      <GuideSection id="context" title="Context: wie logt er in en vanaf waar?">
        <P>
          Een mislukte inlog is ruis. Een mislukte inlog vanaf een adres dat op
          een threat feed staat, is dat niet. LogPulse verrijkt inlogevents met
          zijn IOC-feeds en verhoogt de risicoscore van de gebruiker of het IP.
          Zo valt een aanval vanaf bekende aanvalsinfrastructuur op tussen
          gebruikers die hun wachtwoord vergeten zijn.
        </P>
        <P>
          De retro-hunt zoekt bekende aanvals-IP&rsquo;s 30 dagen terug. Een nieuwe
          indicator kan je dus ook een inlogpoging van weken geleden laten zien.
        </P>
      </GuideSection>

      <GuideSection id="welke-logs" title="Welke logs je nodig hebt">
        <UL>
          <LI>
            <Term>Microsoft Entra ID en Microsoft 365:</Term> koppel de tenant
            onder Sources, Cloud en kies de inlog- en auditlogs. Er hoeft niets
            geïnstalleerd te worden.
          </LI>
          <LI>
            <Term>Okta:</Term> koppel de organisatie onder Sources, Cloud.
          </LI>
          <LI>
            <Term>Servers:</Term> de LogPulse-agent verzamelt sshd en sudo uit{' '}
            <code>/var/log/auth.log</code> of <code>/var/log/secure</code> en
            het Windows Security-log van je domain controllers.
          </LI>
        </UL>
        <Callout title="Wat LogPulse niet vervangt">
          <P>
            LogPulse vervangt geen MFA, wachtwoordmanager of EDR. Het laat zien
            wanneer die maatregelen op de proef worden gesteld en of er een
            faalde. Onder de{' '}
            <GuideLink href="/guides/meldplicht-cyberbeveiligingswet">meldplicht</GuideLink>{' '}
            van de Cyberbeveiligingswet heb je die gegevens ook nodig om binnen
            72 uur te melden wat er gebeurd is.
          </P>
        </Callout>
        <P>
          Het gratis plan bevat deze detecties en vraagt geen creditcard. Bekijk
          het hele plaatje op de pagina{' '}
          <GuideLink href="/threat-intelligence">Threat intelligence</GuideLink>.
        </P>
      </GuideSection>
    </>
  );
}
